import { useMemo, useState } from 'react';
import {
  CircularProgress,
  Pagination,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { Answer, Question } from 'api/requests/communityBuzz/interfaces';
import { QuestionDetails } from '../QuestionDetails';
import { AnswerDetails } from '../AnswerDetails';
import { AnswerFormData, QAEntryModal } from '../QAEntryModal/QAEntryModal';
import { StyledButton, StyledModal } from './components';
import {
  ANSWERS_PER_PAGE_ON_DESKTOP,
  ANSWERS_PER_PAGE_ON_MOBILE,
  COMMUNITY_BUZZ_GA4_EVENTS,
  isBumperAIContributor,
} from '../constants';
import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';

interface QADisplayModalProps {
  open: boolean;
  onClose: () => void;
  question?: Question;
  onSubmitAnswer: (data: AnswerFormData) => Promise<void> | void;
  isLoading?: boolean;
}

export const QADisplayModal = ({
  open,
  onClose,
  question,
  onSubmitAnswer,
  isLoading = false,
}: QADisplayModalProps) => {
  const { isMobile } = useScreenSize();
  const { trackEventGA4 } = useAppConfigContext();
  const [openQAEntryModal, setOpenQAEntryModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const sortedAnswers = useMemo(() => {
    const answers = question?.answers || [];
    if (!answers.length) return [];
    const aiAnswers = answers.filter((answer) =>
      isBumperAIContributor(answer.source, answer.username),
    );
    const nonAiAnswers = answers.filter(
      (answer) => !isBumperAIContributor(answer.source, answer.username),
    );
    return [...aiAnswers, ...nonAiAnswers];
  }, [question?.answers]);
  const answersLength = sortedAnswers.length;
  const answersPerPage = isMobile
    ? ANSWERS_PER_PAGE_ON_MOBILE
    : ANSWERS_PER_PAGE_ON_DESKTOP;

  const count = useMemo(
    () => Math.ceil(answersLength / answersPerPage),
    [answersLength, answersPerPage],
  );
  const paginatedAnswers = useMemo(
    () =>
      sortedAnswers.slice(
        (currentPage - 1) * answersPerPage,
        currentPage * answersPerPage,
      ),
    [sortedAnswers, currentPage, answersPerPage],
  );
  const showPagination = useMemo(
    () => answersLength > answersPerPage,
    [answersLength, answersPerPage],
  );

  const handlePageChange = (e: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleOpenQAEntryModal = () => {
    setOpenQAEntryModal(true);
    const { communityBuzzAddAnswerAction } = COMMUNITY_BUZZ_GA4_EVENTS;
    trackEventGA4({
      event_name: communityBuzzAddAnswerAction.name,
      type: communityBuzzAddAnswerAction.type,
    });
  };

  const handleCloseQAEntryModal = () => {
    setOpenQAEntryModal(false);
  };

  const handleSubmit = (data: AnswerFormData) => onSubmitAnswer(data);

  return (
    <StyledModal open={open} onClose={onClose} hideBackdrop hasCloseIcon>
      {isLoading ? (
        <Stack alignItems="center" py={6} px={4}>
          <CircularProgress />
          <Text mt={2} color="text.secondary">
            Loading question...
          </Text>
        </Stack>
      ) : (
        <Stack>
          <QuestionDetails question={question} isModal />
          <StyledButton
            variant="outlined"
            onClick={handleOpenQAEntryModal}
            disabled={!question}
          >
            Join the conversation
          </StyledButton>
          {paginatedAnswers?.map((answer: Answer, index: number) => (
            <AnswerDetails key={answer?.id ?? index} answer={answer} />
          ))}
          {showPagination && (
            <Stack mt={2}>
              <Pagination
                count={count}
                page={currentPage}
                shape="circular"
                onChange={handlePageChange}
                showFirstButton
                showLastButton
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 2}
                sx={{
                  '&.MuiPagination-root': {
                    display: 'flex',
                    justifyContent: 'center',
                  },
                }}
              />
            </Stack>
          )}
          <QAEntryModal
            isOpen={openQAEntryModal}
            onClose={handleCloseQAEntryModal}
            onSubmit={handleSubmit}
            title="Write an Answer"
            type="Answer"
          />
        </Stack>
      )}
    </StyledModal>
  );
};
